/**
 * TtsPlayer — invisible component that plays TTS audio from chatEvents.
 *
 * Listens for 'tts_audio' events (base64 MP3 chunks) and plays them
 * sequentially. No UI — just audio playback.
 */

import { useEffect, useRef } from 'react';
import chatEvents from '../lib/chatEvents';

export default function TtsPlayer() {
  const queueRef = useRef([]);
  const playingRef = useRef(false);
  const currentRef = useRef(null);

  useEffect(() => {
    function enqueue(b64) {
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: 'audio/mpeg' }));
      queueRef.current.push(url);
      if (!playingRef.current) playNext();
    }

    function playNext() {
      if (!queueRef.current.length) {
        playingRef.current = false;
        currentRef.current = null;
        return;
      }
      playingRef.current = true;
      const url = queueRef.current.shift();
      const audio = new Audio(url);
      currentRef.current = audio;
      audio.onended = audio.onerror = () => {
        URL.revokeObjectURL(url);
        playNext();
      };
      audio.play().catch(() => {
        URL.revokeObjectURL(url);
        playNext();
      });
    }

    function handleAudio(e) {
      const { data } = e.detail;
      if (data) enqueue(data);
    }

    function handleDone() {
      // Queue drains naturally
    }

    function handleUserMessage() {
      // Stop current playback when user sends a new message
      queueRef.current = [];
      playingRef.current = false;
      if (currentRef.current) {
        currentRef.current.pause();
        currentRef.current = null;
      }
    }

    chatEvents.addEventListener('tts_audio', handleAudio);
    chatEvents.addEventListener('tts_done', handleDone);
    chatEvents.addEventListener('user_message', handleUserMessage);

    return () => {
      chatEvents.removeEventListener('tts_audio', handleAudio);
      chatEvents.removeEventListener('tts_done', handleDone);
      chatEvents.removeEventListener('user_message', handleUserMessage);
      // Cleanup
      queueRef.current = [];
      if (currentRef.current) currentRef.current.pause();
    };
  }, []);

  return null; // No UI
}
